import type { BigNumberish } from "@ethersproject/bignumber"
import type { FetchSignerResult, Signer } from "@wagmi/core"
import { useState } from "react"
import { useToast } from "@chakra-ui/react"
import { isAddress } from "@ethersproject/address"
import type { Contract } from "ethers"

import { useAddress } from "web3/hooks/useAddress"
import { GovernorType } from "query/graphql"
import type { GovernorContract, Governor as GovernorQuery } from "query/graphql"
import type { UseCorrectChainErrors } from "web3/helpers/chain"
import { getCorrectChain } from "web3/helpers/chain"
import { getChainIdParams } from "web3/helpers/chainId"
import { getChainReferenceName } from "web3/helpers/chainReference"
import { getGovernorOpenZeppelin } from "web3/hooks/useGovernorOpenZeppelin"
import { getGovernorAlpha } from "web3/hooks/useGovernorAlpha"
import { getGovernorBravo } from "web3/hooks/useGovernorBravo"
import { isValidAccountId } from "web3/helpers/accountId"
import { implementsBravo } from "web3/helpers/validations"
import { useTransaction } from "web3/providers/TransactionProvider"
import type { TransactionOn, TransactionStatus } from "web3/types/transaction"
import { useMe } from "user/providers/MeProvider"
import { useSafeTransactionV2 } from "safe/hooks/useSafeTransactionV2"
import { getGovernorHub } from "web3/hooks/useGovernorHub"
import { isSameAddress } from "web3/helpers/address"
import { useMultichainAccount } from "web3/hooks/useMultichainAccount"

export enum UsePublishProposalErrors {
  Signernotfound = "SIGNER_NOT_FOUND",
  Nonmatchingchain = "NON_MATCHING_CHAIN",
  Undefinedgovernance = "UNDEFINED_GOVERNANCE",
  Invalidgovernanceid = "INVALID_GOVERNANCE_ID",
  Invalidgovernoraddress = "INVALID_GOVERNOR_ADDRESS",
}

type Error = UseCorrectChainErrors | UsePublishProposalErrors

export type PublishProposalFormValues = {
  values: BigNumberish[]
  targets: string[]
  calldatas: string[]
  signatures?: string[]
  description: string
}

type Values<Metadata> = {
  error?: Error
  status: TransactionStatus<Metadata>
  setMetadata: (metadata: Metadata) => void
  isRejected: boolean
  publishProposal: (args: {
    signer?: FetchSignerResult<Signer>
    governance?: GPublishProposal
    formValues: PublishProposalFormValues
    chainReference?: number
    metadata?: Record<string, any>
  }) => void
}

export type GPublishProposal = Pick<GovernorQuery, "id" | "chainId"> & {
  contracts: {
    governor: Pick<GovernorContract, "address" | "type">
  }
}

export const usePublishProposal = <Metadata>(
  {
    on,
  }: {
    on?: TransactionOn<Metadata>
  } = {},
  handlePendingSafeTx?: (values: Record<string, string>) => void,
): Values<Metadata> => {
  const [error, setError] = useState<Error | undefined>(undefined)
  const [isRejected, setIsRejected] = useState<boolean>(false)

  const me = useMe()
  const address = useAddress()
  const { multichainAccount: walletAccount } = useMultichainAccount()

  const canUseSafeTx =
    me?.type === "SAFE" && !isSameAddress(me.address, walletAccount.address)

  const toast = useToast()
  const { status, setMetadata, send } = useTransaction({
    on,
  })

  const {
    proposeMultiGnosisTransaction,
    buildSafeTransaction,
    onPendingSafeTx,
  } = useSafeTransactionV2(
    {
      safeId: me?.id as string,
      accountAddress: address,
    },
    { enabled: me?.type === "SAFE" },
  )

  const publishProposal = async ({
    signer,
    governance,
    formValues,
    chainReference,
    metadata,
  }: {
    signer?: FetchSignerResult<Signer>
    governance?: GPublishProposal
    formValues: PublishProposalFormValues
    chainReference?: number
    metadata?: Record<string, any>
  }): Promise<void> => {
    if (!governance) {
      setError(UsePublishProposalErrors.Undefinedgovernance)

      return
    }

    const { chainId, contracts, id: governanceId } = governance
    const { governor } = contracts
    const { type: governorType, address: governorAddress } = governor

    const { calldatas, description, signatures, targets, values } = formValues

    // signatures are included as a prefix to calldatas and thus should be empty
    // https://github.com/compound-finance/compound-protocol/blob/master/contracts/Timelock.sol#L93
    const emptySignatures = signatures?.map(() => "") ?? []

    const { reference } = getChainIdParams(chainId)
    const chainReferenceName = getChainReferenceName(reference)

    if (!signer) {
      setError(UsePublishProposalErrors.Signernotfound)
      toast({
        title: "No signer found",
        status: "error",
        description: "You should have your wallet connected",
      })

      return
    }

    const governorOz = getGovernorOpenZeppelin(governorAddress, signer)
    const governorAlpha = getGovernorAlpha(governorAddress, signer)
    const governorBravo = getGovernorBravo(governorAddress, signer)
    const governorHub = getGovernorHub(governorAddress, signer)

    const getSafeTxHash = async (
      governorContract: Contract,
      targets: string[],
      values: BigNumberish[],
      calldatas: string[],
      description: string,
      governorAddress: string,
      signatures?: string[],
    ): Promise<string | undefined> => {
      try {
        const safeTxHash = signatures
          ? await proposeMultiGnosisTransaction([
              buildSafeTransaction(
                governorContract,
                "propose",
                [targets, values, signatures, calldatas, description],
                governorAddress,
              ),
            ])
          : await proposeMultiGnosisTransaction([
              buildSafeTransaction(
                governorContract,
                "propose",
                [targets, values, calldatas, description],
                governorAddress,
              ),
            ])

        return safeTxHash
      } catch (error: any) {
        if (error && error.message.includes("user rejected signing")) {
          setIsRejected(true)
          toast({
            status: "error",
            title: "User rejected signature",
            duration: 5000,
          })
        }
      }
    }

    const { isCorrectChain, error: useCorrectChainErrors } = getCorrectChain({
      chainId,
      chainReference,
    })

    if (!isCorrectChain) {
      setError(UsePublishProposalErrors.Nonmatchingchain)

      toast({
        status: "warning",
        title: "Incorrect network",
        description: `You must be connected to the ${chainReferenceName} network to publish a proposal`,
        duration: 5000,
      })

      return
    }

    if (useCorrectChainErrors !== undefined) {
      setError(useCorrectChainErrors)

      throw new Error(
        `useCastVote => "chainId" or "chainReference" or both, are incorrect.
         This should not be happening as they are being gathered from the server and the provider`,
      )
    }

    if (!isAddress(governorAddress)) {
      setError(UsePublishProposalErrors.Invalidgovernoraddress)

      return
    }

    if (!isValidAccountId(governanceId)) {
      setError(UsePublishProposalErrors.Invalidgovernanceid)

      return
    }

    switch (governorType) {
      case GovernorType.Governoralpha: {
        if (canUseSafeTx) {
          const safeTxHash = await getSafeTxHash(
            governorAlpha,
            targets,
            values,
            calldatas,
            description,
            governorAddress,
          )
          onPendingSafeTx(safeTxHash, governanceId, {
            requiredParameters: [
              "targets",
              "values",
              "calldatas",
              "description",
            ],
            expectedMethod: "propose",
          })((values) => handlePendingSafeTx?.({ ...values, ...metadata }))
        } else {
          send(() =>
            governorAlpha.propose(
              targets,
              values,
              emptySignatures,
              calldatas,
              description,
            ),
          )
        }

        break
      }

      case GovernorType.Nounsfork:
      case GovernorType.Governorbravo: {
        /*
            Due to the following bug, we must call `propose` without signatures for OZ governors with
            Bravo compatibilty.

            https://github.com/OpenZeppelin/openzeppelin-contracts/security/advisories/GHSA-m6w8-fq7v-ph4m
          */

        const doesImplementBravo = await implementsBravo(governorOz)

        if (doesImplementBravo) {
          if (canUseSafeTx) {
            const safeTxHash = await getSafeTxHash(
              governorOz,
              targets,
              values,
              calldatas,
              description,
              governorAddress,
            )
            onPendingSafeTx(safeTxHash, governanceId, {
              requiredParameters: [
                "targets",
                "values",
                "calldatas",
                "description",
              ],
              expectedMethod: "propose",
            })((values) => handlePendingSafeTx?.({ ...values, ...metadata }))
          } else {
            send(() =>
              governorOz.propose(targets, values, calldatas, description),
            )
          }

          return
        }

        if (canUseSafeTx) {
          const safeTxHash = await getSafeTxHash(
            governorBravo,
            targets,
            values,
            calldatas,
            description,
            governorAddress,
            emptySignatures.length === 0 ? [""] : emptySignatures,
          )
          onPendingSafeTx(safeTxHash, governanceId, {
            requiredParameters: [
              "targets",
              "values",
              "signatures",
              "calldatas",
              "description",
            ],
            expectedMethod: "propose",
          })((values) => handlePendingSafeTx?.({ ...values, ...metadata }))
        } else {
          send(() =>
            governorBravo.propose(
              targets,
              values,
              emptySignatures.length === 0 ? [""] : emptySignatures,
              calldatas,
              description,
            ),
          )
        }

        break
      }

      case GovernorType.Openzeppelingovernor: {
        if (canUseSafeTx) {
          const safeTxHash = await getSafeTxHash(
            governorOz,
            targets,
            values,
            calldatas,
            description,
            governorAddress,
          )

          onPendingSafeTx(safeTxHash, governanceId, {
            requiredParameters: [
              "targets",
              "values",
              "calldatas",
              "description",
            ],
            expectedMethod: "propose",
          })((values) => handlePendingSafeTx?.({ ...values, ...metadata }))
        } else {
          send(() =>
            governorOz.propose(targets, values, calldatas, description),
          )
        }

        break
      }

      case GovernorType.Hub: {
        if (canUseSafeTx) {
          const safeTxHash = await getSafeTxHash(
            governorHub,
            targets,
            values,
            calldatas,
            description,
            governorAddress,
          )

          onPendingSafeTx(safeTxHash, governanceId, {
            requiredParameters: [
              "targets",
              "values",
              "calldatas",
              "description",
            ],
            expectedMethod: "propose",
          })((values) => handlePendingSafeTx?.({ ...values, ...metadata }))
        } else {
          send(() =>
            governorHub.propose(targets, values, calldatas, description),
          )
        }

        break
      }

      default:
        break
    }

    return
  }

  return {
    error,
    status,
    setMetadata,
    publishProposal,
    isRejected,
  }
}
